import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useData } from '../context/DataContext';

const pageTitles = {
  '/': { title: 'Navavidha Bhakti — Online Puja at Baba Baidyanath Dham, Deoghar', desc: 'Book online pujas at Baba Baidyanath Dham (Deoghar), Kashi, Ujjain & Haridwar. Sankalp in your name & gotra by Vedic Pandit Jis — with live video and Prasad delivery.' },
  '/upcoming-pujas': { title: 'Upcoming Pujas — Navavidha Bhakti', desc: 'Browse all upcoming online pujas at holy tirth kshetras. Shanivar Special, Gupt Navratri, Ganesh Chaturthi and more.' },
  '/about': { title: 'About Us — Navavidha Bhakti', desc: 'Learn about Navavidha Bhakti — a Deoghar-based devotional platform embodying nine forms of Bhakti.' },
  '/contact': { title: 'Contact Us — Navavidha Bhakti', desc: 'Contact Navavidha Bhakti team for puja queries, bookings and support. Based in Deoghar, Jharkhand.' },
  '/privacy-policy': { title: 'Privacy Policy — Navavidha Bhakti', desc: 'Our privacy policy explaining how devotee data is collected, used and safeguarded.' },
  '/terms-and-conditions': { title: 'Terms & Conditions — Navavidha Bhakti', desc: 'Terms and conditions for using Navavidha Bhakti devotional booking services.' },
  '/refund-policy': { title: 'Refund & Cancellation Policy — Navavidha Bhakti', desc: 'Fair and transparent refund/cancellation policy for puja bookings.' },
  '/shipping-policy': { title: 'Shipping & Delivery Policy — Navavidha Bhakti', desc: 'How Prasad and puja items are shipped across India by Navavidha Bhakti.' },
  '/login': { title: 'Devotee Login — Navavidha Bhakti', desc: 'Sign in to your Navavidha Bhakti devotee account.' },
  '/admin/login': { title: 'Admin — Navavidha Bhakti', desc: 'Admin login.' }
};

const DEFAULT_KEYWORDS = 'online puja, baba baidyanath dham, deoghar puja, jyotirlinga puja, hindu ritual online, sankalp puja, vedic pandit online, kashi puja, ujjain puja, navavidha bhakti, prasad home delivery, puja booking india';

function upsertMeta(selector, attrs) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement('meta');
    Object.entries(attrs).forEach(([k, v]) => { if (k !== 'content') el.setAttribute(k, v); });
    document.head.appendChild(el);
  }
  el.setAttribute('content', attrs.content);
}

function upsertLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) { el = document.createElement('link'); el.setAttribute('rel', rel); document.head.appendChild(el); }
  el.setAttribute('href', href);
}

export default function SEOInjector() {
  const location = useLocation();
  const { settings } = useData();
  useEffect(() => {
    const path = location.pathname;
    const seo = settings.seo || {};

    const meta = pageTitles[path] || { title: pageTitles['/'].title, desc: pageTitles['/'].desc };
    const finalTitle = seo.siteTitleOverride && path === '/' ? seo.siteTitleOverride : meta.title;
    const finalDesc = seo.siteDescriptionOverride && path === '/' ? seo.siteDescriptionOverride : meta.desc;
    const keywords = seo.keywords || DEFAULT_KEYWORDS;
    const ogImage = seo.ogImage || 'https://navavidhabhakti.com/brand/logo.png';
    const baseUrl = window.location.origin;
    const url = baseUrl + path;

    document.title = finalTitle;

    upsertMeta('meta[name="description"]', { name: 'description', content: finalDesc });
    upsertMeta('meta[name="keywords"]', { name: 'keywords', content: keywords });
    upsertMeta('meta[name="author"]', { name: 'author', content: 'Navavidha Bhakti' });
    upsertMeta('meta[name="robots"]', { name: 'robots', content: path.startsWith('/admin') || path === '/dashboard' ? 'noindex, nofollow' : 'index, follow' });

    // Open Graph
    upsertMeta('meta[property="og:title"]', { property: 'og:title', content: finalTitle });
    upsertMeta('meta[property="og:description"]', { property: 'og:description', content: finalDesc });
    upsertMeta('meta[property="og:image"]', { property: 'og:image', content: ogImage });
    upsertMeta('meta[property="og:url"]', { property: 'og:url', content: url });
    upsertMeta('meta[property="og:type"]', { property: 'og:type', content: 'website' });
    upsertMeta('meta[property="og:site_name"]', { property: 'og:site_name', content: 'Navavidha Bhakti' });
    upsertMeta('meta[property="og:locale"]', { property: 'og:locale', content: 'en_IN' });

    // Twitter
    upsertMeta('meta[name="twitter:card"]', { name: 'twitter:card', content: 'summary_large_image' });
    upsertMeta('meta[name="twitter:title"]', { name: 'twitter:title', content: finalTitle });
    upsertMeta('meta[name="twitter:description"]', { name: 'twitter:description', content: finalDesc });
    upsertMeta('meta[name="twitter:image"]', { name: 'twitter:image', content: ogImage });

    // Canonical
    upsertLink('canonical', url);

    // Search Console verification tags
    if (seo.googleVerification) upsertMeta('meta[name="google-site-verification"]', { name: 'google-site-verification', content: seo.googleVerification });
    if (seo.bingVerification) upsertMeta('meta[name="msvalidate.01"]', { name: 'msvalidate.01', content: seo.bingVerification });
    if (seo.pinterestVerification) upsertMeta('meta[name="p:domain_verify"]', { name: 'p:domain_verify', content: seo.pinterestVerification });

    // Google Analytics (GA4)
    if (seo.ga4Id && !document.getElementById('ga4-script')) {
      const s1 = document.createElement('script');
      s1.id = 'ga4-script';
      s1.async = true;
      s1.src = `https://www.googletagmanager.com/gtag/js?id=${seo.ga4Id}`;
      document.head.appendChild(s1);

      const s2 = document.createElement('script');
      s2.id = 'ga4-init';
      s2.innerHTML = `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('js',new Date());gtag('config','${seo.ga4Id}');`;
      document.head.appendChild(s2);
    }

    // Google Tag Manager
    if (seo.gtmId && !document.getElementById('gtm-script')) {
      const s = document.createElement('script');
      s.id = 'gtm-script';
      s.innerHTML = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${seo.gtmId}');`;
      document.head.appendChild(s);
    }

    // Facebook Pixel
    if (seo.fbPixelId && !document.getElementById('fb-pixel')) {
      const s = document.createElement('script');
      s.id = 'fb-pixel';
      s.innerHTML = `!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');fbq('init','${seo.fbPixelId}');fbq('track','PageView');`;
      document.head.appendChild(s);
    }

    // JSON-LD Organization + LocalBusiness (only on home)
    if (path === '/') {
      let ld = document.getElementById('org-jsonld');
      if (ld) ld.remove();
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.id = 'org-jsonld';
      script.innerHTML = JSON.stringify({
        '@context': 'https://schema.org',
        '@graph': [
          {
            '@type': 'Organization',
            name: 'Navavidha Bhakti',
            url: baseUrl,
            logo: ogImage,
            sameAs: [],
            contactPoint: {
              '@type': 'ContactPoint',
              telephone: '+91-7250412100',
              contactType: 'Customer Support',
              email: 'navavidhabhakti001@gmail.com',
              areaServed: 'IN',
              availableLanguage: ['en', 'hi']
            }
          },
          {
            '@type': 'LocalBusiness',
            '@id': `${baseUrl}#business`,
            name: 'Navavidha Bhakti',
            image: ogImage,
            telephone: '+91-7250412100',
            email: 'navavidhabhakti001@gmail.com',
            address: {
              '@type': 'PostalAddress',
              streetAddress: 'Williams Town',
              addressLocality: 'Deoghar',
              addressRegion: 'Jharkhand',
              postalCode: '814112',
              addressCountry: 'IN'
            },
            priceRange: '₹₹',
            description: finalDesc
          },
          {
            '@type': 'WebSite',
            url: baseUrl,
            name: 'Navavidha Bhakti',
            potentialAction: {
              '@type': 'SearchAction',
              target: `${baseUrl}/upcoming-pujas?q={search_term_string}`,
              'query-input': 'required name=search_term_string'
            }
          }
        ]
      });
      document.head.appendChild(script);
    }
  }, [location.pathname, settings]);
  return null;
}
